import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, inr, imgUrl } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";
import {
  CalendarClock, Heart, Settings, LogOut, Package, Trash2, User, CheckCircle2, Clock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const STATUS_STYLES = {
  confirmed: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-600",
  completed: "bg-blue-100 text-blue-700",
};

function StatusBadge({ status }) {
  const s = (status || "pending").toLowerCase();
  return (
    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[s] || "bg-stone-100 text-stone-600"}`}>
      {s}
    </span>
  );
}

const TABS = [
  { id: "bookings", label: "My Bookings", icon: CalendarClock },
  { id: "favourites", label: "Favourites", icon: Heart },
  { id: "preferences", label: "Preferences", icon: Settings },
];

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { customer, loading, logout, setCustomer } = useCustomer();
  const [tab, setTab] = useState("bookings");

  useEffect(() => {
    if (!loading && !customer) navigate("/login?next=/account");
  }, [loading, customer, navigate]);

  const { data: bookings = [], isLoading: bLoading } = useQuery({
    queryKey: ["customer-bookings"],
    queryFn: async () => (await api.get("/customer/bookings")).data,
    enabled: !!customer,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });

  if (loading || !customer) return null;

  const firstName = (customer.full_name || "there").split(" ")[0];

  return (
    <div className="px-4 md:px-8 py-6 pb-28">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-primary/10 grid place-items-center">
            <User className="h-6 w-6 text-primary" />
          </div>
          <div>
            <p className="overline text-primary">My account</p>
            <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight" data-testid="dashboard-greeting">
              Hi, {firstName} 👋
            </h1>
          </div>
        </div>
        <button
          data-testid="dashboard-logout"
          onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-1.5 h-10 px-4 rounded-full border border-stone-300 text-sm font-semibold hover:bg-muted transition-colors"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`dashtab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                active ? "bg-primary text-white" : "bg-card border border-stone-200 text-foreground hover:bg-muted"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "bookings" && (
        <BookingsTab bookings={bookings} loading={bLoading} navigate={navigate} />
      )}
      {tab === "favourites" && (
        <FavouritesTab customer={customer} setCustomer={setCustomer} navigate={navigate} />
      )}
      {tab === "preferences" && (
        <PreferencesTab customer={customer} setCustomer={setCustomer} categories={categories} />
      )}
    </div>
  );
}

function BookingsTab({ bookings, loading, navigate }) {
  if (loading) return <p className="text-muted-foreground">Loading your bookings...</p>;
  if (!bookings.length)
    return (
      <div className="text-center py-16" data-testid="no-bookings">
        <CalendarClock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">You have no bookings yet.</p>
        <button
          onClick={() => navigate("/packages")}
          className="mt-4 h-11 px-6 bg-primary text-white rounded-full font-semibold"
        >
          Explore packages
        </button>
      </div>
    );
  return (
    <div className="grid gap-4" data-testid="bookings-list">
      {bookings.map((b) => (
        <div key={b.id} data-testid={`booking-${b.id}`} className="bg-card border border-stone-200 rounded-2xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-display font-bold text-lg">{b.event_type || "Event"}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                <Clock className="h-3.5 w-3.5" />
                {b.event_date ? new Date(b.event_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Date TBD"}
                {b.event_time ? ` · ${b.event_time}` : ""}
              </p>
              {b.venue && <p className="text-xs text-muted-foreground mt-0.5">{b.venue}, {b.city}</p>}
            </div>
            <StatusBadge status={b.status} />
          </div>
          <div className="mt-3 border-t border-stone-100 pt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <span className="text-muted-foreground">Items: <b className="text-foreground">{(b.items || []).length}</b></span>
            <span className="text-muted-foreground">Total: <b className="text-foreground">{inr(b.final_total || b.subtotal)}</b></span>
            <span className="text-muted-foreground">
              Payment:{" "}
              <b className={`${b.payment_status === "paid" ? "text-emerald-600" : b.payment_status === "partially_paid" ? "text-amber-600" : "text-foreground"}`}>
                {(b.payment_status || "unpaid").replace("_", " ")}
              </b>
            </span>
          </div>
          {(b.items || []).length > 0 && (
            <p className="text-xs text-muted-foreground mt-2 truncate">
              {b.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function FavouritesTab({ customer, setCustomer, navigate }) {
  const wishlist = customer.wishlist || [];
  const remove = async (w) => {
    try {
      const { data } = await api.delete(`/customer/wishlist/${w.kind}/${w.item_id}`);
      setCustomer(data);
    } catch {
      toast.error("Could not remove");
    }
  };
  if (!wishlist.length)
    return (
      <div className="text-center py-16" data-testid="no-favourites">
        <Heart className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No saved favourites yet. Tap the heart on any package to save it.</p>
        <button onClick={() => navigate("/packages")} className="mt-4 h-11 px-6 bg-primary text-white rounded-full font-semibold">
          Browse packages
        </button>
      </div>
    );
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="favourites-list">
      {wishlist.map((w) => (
        <div key={`${w.kind}-${w.item_id}`} data-testid={`fav-${w.item_id}`} className="bg-card border border-stone-200 rounded-2xl overflow-hidden flex">
          <div className="w-24 shrink-0 bg-muted">
            {w.image ? <img src={imgUrl(w.image)} alt={w.name} className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center"><Package className="h-6 w-6 text-muted-foreground" /></div>}
          </div>
          <div className="flex-1 p-3 flex flex-col justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wide font-bold text-primary">{w.kind}</span>
              <p className="font-semibold leading-tight">{w.name}</p>
              {w.price > 0 && <p className="text-sm font-display font-bold mt-0.5">{inr(w.price)}</p>}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => navigate(w.kind === "package" ? "/packages" : "/services")}
                className="text-xs font-semibold text-primary"
              >
                View
              </button>
              <button
                data-testid={`fav-remove-${w.item_id}`}
                onClick={() => remove(w)}
                className="ml-auto h-8 w-8 grid place-items-center rounded-full hover:bg-muted text-stone-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PreferencesTab({ customer, setCustomer, categories }) {
  const [form, setForm] = useState({
    email: customer.email || "",
    alt_phone: customer.alt_phone || "",
    address: customer.address || "",
    preferred_occasions: customer.preferred_occasions || [],
  });
  const [saving, setSaving] = useState(false);

  const toggleOcc = (name) =>
    setForm((f) => ({
      ...f,
      preferred_occasions: f.preferred_occasions.includes(name)
        ? f.preferred_occasions.filter((o) => o !== name)
        : [...f.preferred_occasions, name],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/customer/profile", form);
      setCustomer(data);
      toast.success("Preferences saved");
    } catch {
      toast.error("Could not save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 max-w-2xl" data-testid="preferences-form">
      <div className="bg-card border border-stone-200 rounded-2xl p-5">
        <h3 className="font-display font-bold text-lg mb-3">Your details</h3>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Info label="Name" value={customer.full_name} />
          <Info label="Mobile" value={customer.mobile} />
          <Info label="Date of birth" value={customer.dob} />
          <Info label="Age" value={customer.age != null ? `${customer.age} yrs` : "—"} />
          <Info label="Gender" value={(customer.gender || "").replace(/_/g, " ")} />
        </div>
      </div>

      <div className="bg-card border border-stone-200 rounded-2xl p-5">
        <h3 className="font-display font-bold text-lg mb-3">Contact details</h3>
        <div className="grid gap-3">
          <Input data-testid="pref-email" type="email" placeholder="Email address" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input data-testid="pref-altphone" placeholder="Alternate phone (optional)" value={form.alt_phone}
            onChange={(e) => setForm({ ...form, alt_phone: e.target.value })} />
          <Textarea data-testid="pref-address" placeholder="Address (optional)" value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
      </div>

      <div className="bg-card border border-stone-200 rounded-2xl p-5">
        <h3 className="font-display font-bold text-lg mb-1">Preferred occasions</h3>
        <p className="text-sm text-muted-foreground mb-3">We'll tailor recommendations around these.</p>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => {
            const on = form.preferred_occasions.includes(c.name);
            return (
              <button
                key={c.id}
                data-testid={`pref-occasion-${c.slug}`}
                onClick={() => toggleOcc(c.name)}
                className={`flex items-center gap-1 h-9 px-3.5 rounded-full text-sm font-semibold border transition-colors ${
                  on ? "bg-primary text-white border-primary" : "bg-card border-stone-200 hover:bg-muted"
                }`}
              >
                {on && <CheckCircle2 className="h-4 w-4" />} {c.name}
              </button>
            );
          })}
        </div>
      </div>

      <button
        data-testid="pref-save"
        onClick={save}
        disabled={saving}
        className="h-12 px-6 bg-primary text-white rounded-full font-semibold disabled:opacity-60 hover:bg-primary/90 transition-colors justify-self-start"
      >
        {saving ? "Saving..." : "Save preferences"}
      </button>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-semibold capitalize">{value || "—"}</p>
    </div>
  );
}
